import { FC, ReactNode, useState } from 'react';
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import {
    DateField,
    useRecordContext,
    useResourceContext,
    useTranslate,
    useNotify,
    useRefresh,
} from 'react-admin';
import { Box, Stack, Switch } from '@mui/material';

const StyledStaleBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.warning.main,
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
}));

interface StaleBadgeProps {
    children: ReactNode;
    label?: string;
    options?: { showDetails?: boolean; showToggle?: boolean };
    // Overrides the ambient resource (e.g. 'usergroup' for a school relation row nested
    // inside a teacher's RowForm, which otherwise inherits the 'teacher' resource context).
    resource?: string;
    // Called after a successful toggle, in addition to the built-in refresh() - lets a
    // parent (e.g. a school edit dialog) also refresh an outer record it doesn't own.
    onAfterAction?: () => void;
}

// Shows user_data_state (computed, admin_settings.user_data_recheck_days) as a badge near the
// wrapped field. List usage: dot shown only when stale. Edit usage (showDetails/showToggle):
// adds a text label plus a manual FORCE_STALE/CLEAR_STALE toggle bound to the raw stale_queue flag.
const StaleBadge: FC<StaleBadgeProps> = ({
    children,
    options = { showDetails: false, showToggle: false },
    resource: resourceOverride,
    onAfterAction,
}) => {
    const record = useRecordContext();
    const ambientResource = useResourceContext();
    const resource = resourceOverride || ambientResource;
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);

    const isStale = record?.user_data_state === 'stale';
    // Top-level, NOT record.private.stale_queue - the backend's FilterLog (model.js) always
    // strips the private object from teacher/student/pupil responses before they reach the client.
    const staleQueue = record?.stale_queue === true;
    // Switch reflects the VISIBLE state (isStale), not just the raw override flag - otherwise the
    // switch position could silently disagree with the "Tiedot vanhentuneet"/"ajan tasalla" text
    // whenever the account is naturally past the recheck threshold with no manual override set.
    const checked = isStale;
    // Read-only when toggling could not produce any real change: naturally stale by timestamp
    // (no override to clear) means CLEAR_STALE would be a no-op and FORCE_STALE is redundant.
    const readOnly = !staleQueue && isStale;

    const callAction = async (action: 'FORCE_STALE' | 'CLEAR_STALE') => {
        setLoading(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_INFRA_API}/${resource}/${record?.guid}`,
                {
                    method: 'PUT',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: JSON.stringify({ ...record, action }),
                }
            );
            if (!res.ok) throw new Error('action failed');
            notify(
                action === 'FORCE_STALE'
                    ? 'resources.user.stale.forced'
                    : 'resources.user.stale.cleared',
                { type: 'success' }
            );
            refresh();
            onAfterAction?.();
        } catch (e) {
            notify('notifications.ACTION_ERROR', {
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    if (!options.showDetails) {
        // List mode: silent when up-to-date, dot badge only when stale (mirrors SessionBadge).
        if (!isStale) return <>{children}</>;
        return (
            <Stack direction="row" spacing={1}>
                {children}
                <StyledStaleBadge
                    overlap="rectangular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    variant="dot"
                >
                    &nbsp;
                </StyledStaleBadge>
            </Stack>
        );
    }

    // Edit mode: always visible detail block + manual override toggle.
    return (
        <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
                {children}
                <Box
                    sx={{
                        fontSize: '0.9rem',
                        color: isStale ? 'warning.main' : 'text.secondary',
                    }}
                >
                    {translate(
                        isStale
                            ? 'resources.user.stale.stale'
                            : 'resources.user.stale.up_to_date'
                    )}
                </Box>
                {options.showToggle && (
                    <Switch
                        checked={checked}
                        disabled={loading || readOnly}
                        onChange={e =>
                            callAction(
                                e.target.checked ? 'FORCE_STALE' : 'CLEAR_STALE'
                            )
                        }
                        title={translate('resources.user.stale.force_toggle')}
                    />
                )}
                {options.showToggle && readOnly && (
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {translate('resources.user.stale.natural_hint')}
                    </Box>
                )}
            </Stack>
            {record?.user_last_checked && (
                <Box sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {translate('resources.user.stale.last_checked')}:{' '}
                    <DateField
                        source="user_last_checked"
                        showTime
                        locales="fi-FI"
                    />
                </Box>
            )}
        </Stack>
    );
};

export default StaleBadge;
